import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Verifications, Wiki } from '@local/client-contracts';
import { CollectionContextMenuHelper, InvokeModel } from '../../helpers/collection-context-menu-helper';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ContextMenuService } from '@shared/components';
import { isEmbed, isNativeWindow } from '@local/common-web';
import { SwitchViewState, WikiCardPageMode, WikiCardPermission, WikiCardViewMode } from '../../models/wiki-card-model';
import { CollectionOptionsId } from '../../models';
import { WorkspacesService } from 'src/app/bar/services';
type SwitchViewContent = {
  text?: string;
  icon?: string;
  tooltip?: string;
};
@UntilDestroy()
@Component({
  selector: 'wiki-card-header-actions',
  templateUrl: './wiki-card-header-actions.component.html',
  styleUrls: ['./wiki-card-header-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiCardHeaderActionsComponent implements AfterViewInit {
  private readonly isNative = isNativeWindow();
  readonly isEmbed: boolean = isEmbed();

  private _wikiCard: Wiki.Card;
  private _viewMode: WikiCardViewMode;
  private _permissionRole: WikiCardPermission;
  private collectionContextMenuHelper: CollectionContextMenuHelper;
  private openExternalClicked: boolean;

  switchViewState: SwitchViewState;
  switchViewContent: SwitchViewContent;
  contextMenuActive: boolean;
  switchButtonActive: boolean;

  @Input() set wikiCard(value: Wiki.Card) {
    this._wikiCard = value;
    this.updateSwitchView();
  }
  @Input() set viewMode(value: WikiCardViewMode) {
    this._viewMode = value;
    this.updateSwitchView();
  }
  @Input() set permissionRole(value: WikiCardPermission) {
    this._permissionRole = value;
    this.updateSwitchView();
  }
  @Input() pageMode: WikiCardPageMode;
  @Input() updatedVerification: Verifications.Verification;
  @Input() showVerificationIndications: boolean;
  @Input() showCloseButton: boolean;

  //menu
  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;

  //external
  @Output() openExternal = new EventEmitter<boolean>();
  @Output() closePopup = new EventEmitter<boolean>();
  @Output() contextOpen = new EventEmitter<void>();
  @Output() switchView = new EventEmitter<SwitchViewState>();
  @Output() onInvoke = new EventEmitter<InvokeModel>();

  get wikiCard(): Wiki.Card {
    return this._wikiCard;
  }

  get viewMode(): WikiCardViewMode {
    return this._viewMode;
  }

  get permissionRole(): WikiCardPermission {
    return this._permissionRole;
  }

  private get isWindowMode() {
    return this.pageMode === 'window';
  }

  private get isDraftMode() {
    return this.viewMode === 'draft';
  }

  get expandOrCollapseIcon() {
    if (!this.isWindowMode) {
      return 'expand2';
    }
    return this.openExternalClicked ? 'contract2' : 'expand2';
  }

  get expandOrCollapseTooltip() {
    if (!this.isWindowMode) {
      return 'Full size';
    }
    return this.openExternalClicked ? 'Collapse' : 'Expand';
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private eventsService: EventsService,
    private contextMenuService: ContextMenuService,
    private keyboardService: KeyboardService,
    private hubService: HubService,
    private workspaceService: WorkspacesService
  ) {}

  ngAfterViewInit(): void {
    this.initContextMenuHelper();
    this.initContextMenuHelperListeners();
  }

  onOpenExternal() {
    this.openExternalClicked = !this.openExternalClicked;
    this.openExternal.emit(this.openExternalClicked);
    if (this.isEmbed) {
      this.openExternalClicked = false;
    }
  }

  ///ContextMenu
  private initContextMenuHelper(): void {
    this.collectionContextMenuHelper = new CollectionContextMenuHelper(
      this.contextMenuService,
      this.keyboardService,
      this.actionsRef,
      this.hubService,
      this.workspaceService
    );
  }

  private initContextMenuHelperListeners() {
    this.collectionContextMenuHelper.invoke$
      .pipe(untilDestroyed(this))
      .subscribe(({ item, trigger }) => this.onInvoke.emit({ item, trigger }));
  }

  async openContextMenu(event: MouseEvent) {
    this.contextOpen.emit();
    const removedItems = this.removedContextMenuItems();
    const view = this.isDraftMode ? 'draft' : this.permissionRole === 'read' ? 'view' : 'edit';
    const ref = await this.collectionContextMenuHelper.open(event, { pinBy: null }, `wiki-card-${view}`, false, removedItems);
    this.contextMenuActive = true;
    ref.destroy$.subscribe(() => {
      this.contextMenuActive = false;
      this.cdr.markForCheck();
    });
    this.eventsService.event('collections.open_menu', {
      label: 'mouse_click',
      location: { title: `cards/Wiki/${this.wikiCard?.id}` },
    });
  }

  private updateSwitchView() {
    this.switchViewState = this.getSwitchViewState();
    const getEditText = () => (this.pageMode === 'inline' ? null : 'Edit');
    switch (this.switchViewState) {
      case 'closeDraft': {
        this.switchViewContent = {
          text: 'Close draft',
        };
        break;
      }
      case 'editCard': {
        this.switchViewContent = {
          text: getEditText(),
          tooltip: 'Edit card',
          icon: 'edit',
        };
        break;
      }
      case 'editDraft': {
        this.switchViewContent = {
          text: getEditText(),
          tooltip: 'Edit draft',
          icon: 'draft',
        };
        break;
      }
      default: {
        this.switchViewContent = null;
        break;
      }
    }
    this.cdr.markForCheck();
  }

  private getSwitchViewState(): SwitchViewState {
    if (this.permissionRole === 'read' || !this.wikiCard) {
      return;
    }
    switch (this.viewMode) {
      case 'draft': {
        if (!this.wikiCard.publishedTime) {
          return;
        }
        return 'closeDraft';
      }
      case 'published': {
        return this.wikiCard.draft ? 'editDraft' : 'editCard';
      }
    }
  }

  private removedContextMenuItems(): CollectionOptionsId[] {
    if (this.isDraftMode) {
      return !this.wikiCard.publishedTime || (this.isNative && !this.isWindowMode) ? ['view_publish'] : [];
    }
    const removedItems: CollectionOptionsId[] = [];
    if (this.showVerificationIndications) {
      if (!this.updatedVerification?.policy?.isVerifier) {
        removedItems.push('verify', 'unverify');
        if (!this.updatedVerification) {
          removedItems.push('request_verification');
        }
      } else if (this.updatedVerification.status === 'Verified') {
        removedItems.push('verify');
      } else {
        removedItems.push('unverify');
      }
    } else {
      removedItems.push('verify', 'unverify', 'verification_details', 'request_verification');
    }
    if (this.isWindowMode || this.isEmbed) {
      removedItems.push('version_history');
    }
    return removedItems;
  }

  onSwitchView() {
    this.switchView.emit(this.switchViewState);
    this.switchButtonActive = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.switchButtonActive = false;
      this.cdr.markForCheck();
    }, 0);
  }
}
